import type { Root, Declaration, Rule, AtRule } from 'postcss'
import { rule } from 'postcss'
import { ListMap, getDeclarationNodes } from '../helpers'
import generateRandomVariableName, { VAR_LENGTH } from './generateRandomVariableName'

const CSS_VARIABLE_REGEX = /var\(--.+\)/
const BROWSER_PREFIXES = ['-webkit-', '-moz-', '-o-', '-ms-']

const toCssVariable = (value: string) => `--${value}`

const variablizePlugin = () => {
	const longStringsMap = new ListMap<Declaration>()
	const existingVariableNames = new Set()
	let rootVariablesNode: Rule
	let root: Root

	const minimumLength = () => 'var()'.length + VAR_LENGTH

	const ensureRootVariableNode = () => {
		if (rootVariablesNode! || !longStringsMap.hasDuplicates) {
			return
		}

		rootVariablesNode = rule({
			selector: ':root',
		})

		root!.prepend(rootVariablesNode)
	}

	const convertToVariables = () => {
		longStringsMap.iterateDuplicates((declarations, value) => {
			const variableName = generateRandomVariableName()
			const cssVariableName = toCssVariable(variableName)

			rootVariablesNode!.append({
				prop: cssVariableName,
				value,
			})

			existingVariableNames.add(cssVariableName)

			declarations.forEach((declaration: Declaration) => {
				declaration.value = `var(${cssVariableName})`
			})
		})
	}

	return {
		postcssPlugin: 'variablize',
		Declaration: {
			'*': (d: Declaration) => {
				// A very very basic check, that ensures using a variable will not take up more bytes than keeping values as is
				const isLongEnough = d.value.length > minimumLength()
				const isVariableDeclaration = d.prop.startsWith('--')
				const hasBrowserPrefix = BROWSER_PREFIXES.some((prefix) => d.value.startsWith(prefix))
				const containsAnotherVariable = CSS_VARIABLE_REGEX.test(d.value)
				const isFontFaceDeclaration = d.parent
					? d.parent.type === 'atrule' && (d.parent as AtRule).name === 'font-face'
					: false
				const containsUndefined = d.value.includes('undefined')

				if (
					!isLongEnough ||
					isVariableDeclaration ||
					hasBrowserPrefix ||
					containsAnotherVariable ||
					isFontFaceDeclaration ||
					containsUndefined
				) {
					return
				}

				longStringsMap.add(d.value, d)
			},
		},
		Rule: {
			'*': (r: Rule) => {
				// If there's already an existing :root node, might as well use it
				if (r.selector === ':root' && r.parent?.type === 'root') {
					rootVariablesNode = r

					getDeclarationNodes(r).forEach(({ prop }) => existingVariableNames.add(prop))
				}
			},
		},
		Once(rootNode: Root) {
			root = rootNode
		},
		OnceExit() {
			ensureRootVariableNode()
			convertToVariables()
		},
	}
}

variablizePlugin.postcss = true as true

export default variablizePlugin
